import { CombinedState, createSelector } from '@reduxjs/toolkit'
import { useLDClient } from '@afterpaytouch/feature'
import { useEffect, useState } from 'react'
import { Cohort } from '@afterpaytouch/portal-api'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import {
  CheckoutAppSelector,
  CheckoutAppState,
  CheckoutAppStore,
  ExperimentsState,
  triggerExperiment,
  useAppDispatch,
  useAppSelector,
  useCohort,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useIsEmptyCheckoutToken,
} from '../'
import { Experiment, Experiments, ExperimentValue, getExperiment, KnownExperiment } from '../../model'
import { useStore } from 'react-redux'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'

export const experimentsSelector: CheckoutAppSelector<ExperimentsState> = (state) => state.experiments

export const activeExperiments: CheckoutAppSelector<string> = createSelector(experimentsSelector, ({ activeExperiments }) => {
  return Object.entries(activeExperiments)
    .map(([experiment, value]) => `${experiment}${value}`)
    .join(',')
})

const activeExperimentsSelector = (state: CombinedState<CheckoutAppState>): Partial<Record<KnownExperiment, ExperimentValue>> => {
  return state.experiments.activeExperiments
}

export const useExperimentsState = (): ExperimentsState => useAppSelector(experimentsSelector)

export const useActiveExperiments = (): string => useAppSelector(activeExperiments)

export const useTriggerExperimentEvent = (): ((params: Experiment, customEventProperties?: Record<string, unknown>) => void) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const store: CheckoutAppStore = useStore()
  const isEmptyCheckoutToken: boolean = useIsEmptyCheckoutToken()
  return ({ name, value, control }: Experiment, customEventProperties?: Record<string, unknown>): void => {
    const activeExperiments = activeExperimentsSelector(store.getState())
    const analyticsFired = name in activeExperiments
    if (!analyticsFired && !isEmptyCheckoutToken) {
      dispatch(triggerExperiment([name, value]))
      logEvent('Triggered Experiment', {
        experimentName: Experiments[name],
        experimentValue: value,
        experimentControl: control,
        experimentFlag: `${name}${value}`,
        ...customEventProperties,
      })
    }
  }
}

export const useExperimentTrigger = (experiment: Experiment): ((customEventProperties?: Record<string, unknown>) => void) => {
  const [fire, setFired] = useState(false)
  const [customEventProperties, setCustomEventProperties] = useState({})
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (experiment.active && !experiment.loading && fire) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }, [experiment.name, experiment.active, experiment.value, fire, triggerExperimentEvent, experiment, customEventProperties])

  return (customEventProperties) => {
    setFired(true)
    if (customEventProperties !== undefined) {
      setCustomEventProperties(customEventProperties)
    }
  }
}

// delays experiment trigger until experiment is passed in
export const useDelayedExperimentTrigger = (): ((updatedExperiment: Experiment) => void) => {
  const [{ isFired, givenExperiment }, setFiredExperiment] = useState<{ isFired: boolean; givenExperiment: Experiment | undefined }>({
    isFired: false,
    givenExperiment: undefined,
  })
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (givenExperiment === undefined) {
      return
    }

    if (givenExperiment.active && !givenExperiment.loading && isFired) {
      triggerExperimentEvent(givenExperiment)
    }
  }, [givenExperiment, givenExperiment?.name, givenExperiment?.active, givenExperiment?.value, givenExperiment?.control, isFired, triggerExperimentEvent])

  return (experiment) =>
    setFiredExperiment({
      isFired: true,
      givenExperiment: experiment,
    })
}

export function useExperiment(name: KnownExperiment, fireEvent: boolean = true, customisedExperiment: Experiment | null = null): Experiment {
  const client = useLDClient()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { activeExperiments } = useExperimentsState()
  let experiment = customisedExperiment
  if (experiment === null) {
    const ldValue = client?.variationDetail(Experiments[name], false)
    experiment = getExperiment(name, ldValue, activeExperiments[name])
  }

  const experimentTrigger = useExperimentTrigger(experiment)

  useEffect(() => {
    if (fireEvent && consumerFeatureFlagReady) {
      experimentTrigger()
    }
  }, [consumerFeatureFlagReady, experimentTrigger, fireEvent])

  return experiment
}

export const useIsEligibleForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const triggerExperiment = useExperimentTrigger(experiment)
  const { value: addPaymentMethodFlowExperimentValue } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()

  const eligibilityConditions = [addPaymentMethodFlowExperimentValue === 'A', cohort !== Cohort.CashOnly, !isBronteDesignLanguage, !isPaymentMethodEligible]
  const isEligible = eligibilityConditions.every((condition) => condition)

  useEffect(() => {
    if (isEligible) {
      triggerExperiment()
    }
  }, [isEligible])

  return isEligible
}

export const usePaymentOptionHideAprExperiment = (fireEvent: boolean = true): Experiment => {
  return useExperiment('PaymentOptionHideApr', fireEvent)
}

export const useExpandableLightweightPaymentPlans = (): boolean => {
  const experiment = useExperiment('ExpandableLightweightPaymentPlans', true)
  return experiment.active && !experiment.control
}
